import {
	getListSystemTemplatesQueryOptions,
	getListOpportunityTemplatesQueryOptions,
	deleteOpportunityTemplate,
} from "@fp/admin-api-client/client";
import type { OpportunityTemplateDto } from "@fp/admin-api-client/types/opportunityTemplateDto.js";
import { Tabs } from "@fp/ui/components/application/tabs/tabs";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { OpportunityTemplateList } from "@fp/ui/components/opportunities/opportunity-template-list";
import { FloatingAddButton } from "@fp/ui/components/base/buttons/floating-add-button";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { type Key, useState } from "react";
import { toast } from "sonner";

interface TemplateSelectionPageProps {
	systemTemplates: OpportunityTemplateDto[];
	orgTemplates: OpportunityTemplateDto[];
	onOrgTemplateDeleted: () => void;
}

const tabs = [
	{ id: "my-templates", label: "My Templates" },
	{ id: "community", label: "Community" },
];

export const TemplateSelectionPageContent = ({
	systemTemplates,
	orgTemplates,
	onOrgTemplateDeleted,
}: TemplateSelectionPageProps) => {
	const router = useRouter();
	const [selectedTabIndex, setSelectedTabIndex] = useState<Key | null>(orgTemplates.length > 0 ? "my-templates" : "community");
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleSelectedTabChange = async (key: Key | null) => {
		setSelectedTabIndex(key);
	};

	const onTemplateSelect = async (templateId: string) => {
		router.navigate({
			to: "/opps/new",
			search: (search) => ({
				...search,
				template: templateId,
			}),
		});
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return;
		setIsDeleting(true);
		try {
			await deleteOpportunityTemplate(deleteTarget);
			setDeleteTarget(null);
			toast.success("Template deleted");
			onOrgTemplateDeleted();
		} catch {
			toast.error("Failed to delete template");
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<>
			<Tabs
				selectedKey={selectedTabIndex as number}
				onSelectionChange={handleSelectedTabChange}
			>
				<Tabs.List
					type="button-brand"
					items={tabs}
					fullWidth={true}
					className="gap-1"
				>
					{(tab) => <Tabs.Item {...tab} />}
				</Tabs.List>
			</Tabs>
			<section className="flex flex-col space-y-4">
				{selectedTabIndex === "community" && (
					<OpportunityTemplateList
						items={systemTemplates}
						onItemSelect={onTemplateSelect}
					/>
				)}
				{selectedTabIndex === "my-templates" && (
					<>
						{orgTemplates.length === 0 ? (
							<div className="text-center py-8 space-y-4">
								<p className="text-sm font-medium text-secondary">No saved templates yet</p>
								<p className="text-sm text-quaternary">Create an Opp and save it as a template to reuse later.</p>
							</div>
						) : (
							<OpportunityTemplateList
								items={orgTemplates}
								onItemSelect={onTemplateSelect}
								onItemDelete={(id) => setDeleteTarget(id)}
							/>
						)}
					</>
				)}
			</section>
			{selectedTabIndex === "my-templates" && (
				<div className="fixed bottom-24 left-0 right-0 flex justify-end pointer-events-none max-w-screen-sm mx-auto px-6">
					<FloatingAddButton
						size="fab"
						onClick={() => router.navigate({ to: "/opps/new", state: { returnTo: "/opps/templates" } })}
						className="pointer-events-auto"
					/>
				</div>
			)}
			<ConfirmationModal
				isOpen={deleteTarget !== null}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDeleteConfirm}
				title="Delete Template"
				message="Are you sure you want to delete this template?"
				confirmText="Delete"
				cancelText="Cancel"
				confirmColor="primary-destructive"
				isLoading={isDeleting}
			/>
		</>
	);
};

export const Route = createFileRoute("/opps/templates")({
	component: RouteComponent,
	loader: async ({ context: { queryClient } }) => {
		const [systemData, orgData] = await Promise.all([
			queryClient.fetchQuery(
				getListSystemTemplatesQueryOptions({ page: 1, pageSize: 50 }),
			),
			queryClient.fetchQuery(
				getListOpportunityTemplatesQueryOptions({ page: 1, pageSize: 50 }),
			),
		]);

		return {
			systemTemplates: systemData?.data.items ?? [],
			orgTemplates: orgData?.data.items ?? [],
		};
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const { systemTemplates, orgTemplates } = Route.useLoaderData();
	const router = useRouter();

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Templates"
				subtitle="Get started quickly with pre-built or saved templates"
				parent={{ url: "/opps", label: "Opps" }}
			/>
			<PageContentContainer className="space-y-4">
				<TemplateSelectionPageContent
					systemTemplates={systemTemplates}
					orgTemplates={orgTemplates}
					onOrgTemplateDeleted={() => router.invalidate()}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}